import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form, Row, Col, Table, Alert, ButtonGroup, Spinner } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { createCreditNote, getInvoicesByAccount } from "../../afinanceapi";
import { getAccountByPatientId } from "../../api";
import SearchAccounts from "../SearchAccounts/SearchAccounts";
import { FaCheck, FaTimes } from "react-icons/fa";
import "./CreateInvoice.css";

export default function CreditNote({
  show,
  onHide = () => {},
  account,
  patientId,
  invoiceNumber,
}) {
  const [accounts, setAccounts] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [creditNoteAmount, setCreditNoteAmount] = useState("");
  const [accountSelected, setAccountSelected] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [paymentMethod, setPaymentMethod] = useState();
  const [creditNoteTo, setCreditNoteTo] = useState("");
  const [totalUnallocated, setTotalUnallocated] = useState(0);
  const [closeAlert, setCloseAlert] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const navigate = useNavigate();
  const [selectedPractice, setSelectedPractice] = useState("");
  const [email, setEmail] = useState();
  const [selectedInvoiceAmounts, setSelectedInvoiceAmounts] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creditNoteDate, setCreditNoteDate] = useState(() => {
    // initialize to YYYY-MM-DD for today in the local timezone
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  // Add new state to track which payment sources are selected for each invoice
  const [selectedPaymentSources, setSelectedPaymentSources] = useState({});

  // Track outstanding balances for each invoice
  const [outstandingBalances, setOutstandingBalances] = useState({});

  // instead of useState([]):
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [selectedLineCells, setSelectedLineCells] = useState({});
  const [customCreditPatient, setCustomCreditPatient] = useState("");
  const [customCreditMedAid, setCustomCreditMedAid] = useState("");

  useEffect(() => {
    setAccountSelected(account || null);
    setEmail(account?.accountEmail || "");
  }, [account]);

  useEffect(() => {
    if (!show) {
      setSelectedInvoices([]);
      setInvoiceList([]);
      setCreditNoteAmount(0);
      setReferenceNumber("");
      setSelectedPaymentSources({});
      setOutstandingBalances({});
      setCustomCreditPatient("");
      setCustomCreditMedAid("");
    }
  }, [show]);

  useEffect(() => {
    async function fetchData() {
      try {
        if (!account) {
          const data = await getAccountByPatientId(patientId);
          setAccounts(data);
          setAccountSelected(data);
        } else {
          setAccounts([account]);
        }
      } catch (error) {
        console.error(error);
        setAccounts([]);
      }
    }
    fetchData();
  }, [account]);

  const practiceOptions = Array.from(
    new Map(
      invoiceList
        .map((inv) => inv?.practice)
        .filter((p) => p && p.practiceName && p.practiceNumber)
        .map((p) => [p.practiceNumber, p]) // ensures uniqueness by practiceNumber
    ).values()
  );

  useEffect(() => {
    if (practiceOptions?.length > 0 && !selectedPractice) {
      setSelectedPractice(practiceOptions[0]?.practiceNumber); // default selection
    }
  }, [invoiceList]);

  useEffect(() => {
    // Fix for the fetchInvoices function in CreateReceiptModal.js

    async function fetchInvoices() {
      if (!accountSelected) return;
      try {
        const data = await getInvoicesByAccount(accountSelected._id);

        // Calculate outstanding balances for each invoice
        const balances = {};

        data.forEach((invoice) => {
          const ob = invoice.outstandingBalance || {};

          balances[invoice._id] = {
            patientOutstanding: ob.patientOutstanding ?? 0,
            medAidOutstanding: ob.medAidOutstanding ?? 0,
            totalOutstanding: ob.totalOutstanding ?? 0,
            originalTotal: invoice.totalAmount ?? 0,
            paidAmount: ob.paidAmount ?? 0,
          };
        });
        setOutstandingBalances(balances);
        setInvoiceList(data);

        if (invoiceNumber) {
          const match = data.find((inv) => inv.invoiceNumber === invoiceNumber);
          if (match) {
            setSelectedInvoices([match._id]);
            // Set default payment source based on current "Paid By" selection
            setSelectedPaymentSources({
              [match._id]:
                !creditNoteTo || creditNoteTo === "patient" ? "patient" : "medicalAid",
            });
          }
        }

      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoiceList([]);
        setOutstandingBalances({});
      }
    }

    fetchInvoices();
  }, [accountSelected, invoiceNumber, creditNoteTo]);

  const parseCustom = (v) => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

  const toggleInvoice = (invoiceId) => {
    setCustomCreditPatient("");
    setCustomCreditMedAid("");
    setSelectedInvoiceId(
      (prev) =>
        prev === invoiceId
          ? null // close it
          : invoiceId // open this one
    );
  };

  const toggleLineCell = (invoiceId, lineNumber, type) => {
    // 1️⃣ find the invoice + line so we know the amount
    const invoice = invoiceList.find((inv) => inv._id === invoiceId);
    if (!invoice) return;
    const line = invoice.lines.find((l) => l.lineNumber === lineNumber);
    if (!line) return;
    const amount = type === "patient" ? line.patientAmount : line.medAidAmount;
    if (!amount) return; // nothing to select

    // 2️⃣ build the new line-selection map
    let newSelections = {};
    if (selectedInvoiceId === invoiceId) {
      // same invoice: copy existing so we can toggle one line
      newSelections = { ...selectedLineCells };
    }
    // otherwise a new invoice: start fresh (empty)

    const key = `${invoiceId}_${lineNumber}`;
    if (newSelections[key] === type) {
      // it was already selected → unselect it
      delete newSelections[key];
    } else {
      // not selected → select it
      newSelections[key] = type;
    }

    // 3️⃣ decide the new "open" invoice
    const hasCustom = parseCustom(customCreditPatient) > 0 || parseCustom(customCreditMedAid) > 0;
    const hasAny = Object.keys(newSelections).length > 0 || hasCustom;
    const newInvoiceId = hasAny ? invoiceId : null;

    // 4️⃣ update your line-level state
    setSelectedLineCells(newSelections);
    setSelectedInvoiceId(newInvoiceId);
    setCreditNoteTo(type);

    // Clear custom amount in opposite direction
    if (type === "patient") setCustomCreditMedAid("");
    else setCustomCreditPatient("");

    // 5️⃣ mirror that into your invoice-level arrays
    if (newInvoiceId) {
      // always only one invoice in the list
      setSelectedInvoices([invoiceId]);
      // for simplicity, pick the last‐clicked line's type as the invoice’s source
      setSelectedPaymentSources({ [invoiceId]: type });
      // sum up all selected lines for this invoice
      const sum = Object.entries(newSelections).reduce(
        (tot, [cellKey, cellType]) => {
          const [, lnStr] = cellKey.split("_");
          const ln = invoice.lines.find((l) => String(l.lineNumber) === lnStr);
          return ln
            ? tot +
                (cellType === "patient" ? ln.patientAmount : ln.medAidAmount)
            : tot;
        },
        0
      );
      setSelectedInvoiceAmounts({ [invoiceId]: parseFloat(sum.toFixed(2)) });
      const rounded = parseFloat(sum.toFixed(2));
      setSelectedInvoiceAmounts({ [invoiceId]: rounded });
      // ✏️ mirror into creditNoteAmount so your useEffect sees it
      setCreditNoteAmount(rounded.toFixed(2));
    } else {
      // nothing selected → clear invoice-level
      setSelectedInvoices([]);
      setSelectedInvoiceAmounts({});
      setCreditNoteAmount("0.00");
    }
  };

  const handleCustomChange = (e) => {
    const { name, value } = e.target;
    let sanitized = value.replace(/[^0-9.,]/g, "");
    const trailingSep = /[.,]$/.test(sanitized);
    const sepIndex = sanitized.search(/[.,]/);

    let intPart, decPart;
    if (sepIndex >= 0) {
      intPart = sanitized.slice(0, sepIndex);
      decPart = sanitized.slice(sepIndex + 1);
    } else {
      intPart = sanitized;
      decPart = "";
    }

    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    let formatted = intPart;
    if (sepIndex >= 0) {
      formatted += ".";
      if (!trailingSep) formatted += decPart.slice(0, 2);
    }

    if (name === "customCreditPatient") {
      setCustomCreditPatient(formatted ? `R ${formatted}` : "");
    } else if (name === "customCreditMedAid") {
      setCustomCreditMedAid(formatted ? `R ${formatted}` : "");
    }
  };

  const handleCustomBlur = (source) => {
    const value =
      source === "patient"
        ? parseCustom(customCreditPatient)
        : parseCustom(customCreditMedAid);

    if (value > 0) {
      setCreditNoteTo(source);
      if (source === "patient") setCustomCreditMedAid("");
      else setCustomCreditPatient("");
    } else {
      if (source === "patient") setCustomCreditPatient("");
      else setCustomCreditMedAid("");
      const otherValue =
        source === "patient"
          ? parseCustom(customCreditMedAid)
          : parseCustom(customCreditPatient);
      const hasLines = Object.keys(selectedLineCells).length > 0;
      if (!hasLines && otherValue <= 0) {
        setCreditNoteTo("");
      }
    }
  };

  const totalSelectedAmount = useMemo(() => {
    if (!selectedInvoiceId) return 0;

    const invoice = invoiceList.find((inv) => inv._id === selectedInvoiceId);
    if (!invoice) return 0;

    const lineSum = Object.entries(selectedLineCells)
      .filter(([key]) => key.startsWith(selectedInvoiceId + "_"))
      .reduce((sum, [key, type]) => {
        const [, lineNumStr] = key.split("_");
        const line = invoice.lines.find(
          (l) => String(l.lineNumber) === lineNumStr
        );
        if (!line) return sum;

        const amt =
          type === "patient" ? line.patientAmount || 0 : line.medAidAmount || 0;

        return sum + amt;
      }, 0);

    const customSum = parseCustom(customCreditPatient) + parseCustom(customCreditMedAid);
    return lineSum + customSum;
  }, [selectedLineCells, selectedInvoiceId, invoiceList, customCreditPatient, customCreditMedAid]);

  const handlePaymentMethod = (e) => {
    const { name } = e.target;
    setPaymentMethod(name);
  };

  useEffect(() => {
    // 1️⃣ opening balance as a number
    const openingBalance =
      account?.unallocatedBalance != null
        ? Number(account.unallocatedBalance)
        : 0;

    // 2️⃣ start “remaining”:
    //    - for credit‐notes (i.e. paid invoices), we add the totalSelectedAmount
    //    - for unallocated‐funds, we subtract it
    let remaining =
      paymentMethod === "unallocatedFunds"
        ? openingBalance + totalSelectedAmount
        : openingBalance;

    // 3️⃣ build a single‐invoice allocation if that invoice is Paid
    const newAllocations = {};
    if (selectedInvoiceId) {
      const inv = invoiceList.find((i) => i._id === selectedInvoiceId);
      if (inv?.status === "Paid") {
        newAllocations[selectedInvoiceId] = {
          creditNoteAmount: parseFloat(totalSelectedAmount.toFixed(2)),
          paymentSource: selectedPaymentSources[selectedInvoiceId],
        };
      }
    }

    // 4️⃣ commit
    setAllocations(newAllocations);
    setTotalUnallocated(parseFloat(remaining.toFixed(2)));
  }, [
    totalSelectedAmount, // ← swap this in for creditNoteAmount
    paymentMethod,
    selectedInvoiceId,
    selectedPaymentSources,
    invoiceList,
    outstandingBalances, // if you ever use it here
    account?.unallocatedBalance,
  ]);

  function validateCreditNote() {
    const newErrors = {};

    if (!totalSelectedAmount)
      newErrors.totalSelectedAmount = "Please select invoice lines to credit and/or enter a custom credit amount.";
  //  if (!paymentMethod) newErrors.paymentMethod = "Select a payment method.";
    if (!referenceNumber)
      newErrors.referenceNumber = "Please enter a reference.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleCreditNoteCreate = async () => {
    if (isSubmitting) return;
    if (!validateCreditNote()) {
      return; // validation failed, errors are set
    }

    // 2️⃣ Pull in openingBalance (string → number)
    const openingBalance =
      accountSelected.unallocatedBalance != null
        ? parseFloat(accountSelected.unallocatedBalance)
        : 0;

    // 3️⃣ Build your invoice‐line entries
    const invoice = invoiceList.find((inv) => inv._id === selectedInvoiceId);
    if (!invoice) {
      return alert("Selected invoice not found!");
    }
    const creditNoteInvoiceLines = Object.entries(selectedLineCells)
      // only lines for this invoice
      .filter(([key]) => key.startsWith(selectedInvoiceId + "_"))
      .map(([key, type]) => {
        const [, lnStr] = key.split("_");
        const line = invoice.lines.find((l) => String(l.lineNumber) === lnStr);
        const amount =
          type === "patient" ? line.patientAmount : line.medAidAmount;

        // recalc new invoice balances for this line
        const curBal = outstandingBalances[selectedInvoiceId] || {
          patientOutstanding: 0,
          medAidOutstanding: 0,
          totalOutstanding: 0,
          paidAmount: 0,
        };
        const updated = { ...curBal };
        if (type === "patient") {
          updated.patientOutstanding = Math.max(
            0,
            curBal.patientOutstanding - amount
          );
        } else {
          updated.medAidOutstanding = Math.max(
            0,
            curBal.medAidOutstanding - amount
          );
        }
        updated.totalOutstanding =
          updated.patientOutstanding + updated.medAidOutstanding;
        updated.paidAmount = curBal.paidAmount + amount;

        return {
          _id: selectedInvoiceId,
          invoiceNumber: invoice.invoiceNumber,
          invoiceDate: invoice.invoiceDate,
          creditNoteAmount: parseFloat(amount.toFixed(2)),
          paymentSource: creditNoteTo,
          creditNoteReference: referenceNumber,
          creditNoteDate,
          outstandingBalance: {
            patientOutstanding: updated.patientOutstanding,
            medAidOutstanding: updated.medAidOutstanding,
            totalOutstanding: updated.totalOutstanding,
            paidAmount: updated.paidAmount,
          },
        };
      });

    // Append custom credit amount if entered
    const customAmountValue =
      creditNoteTo === "patient"
        ? parseCustom(customCreditPatient)
        : parseCustom(customCreditMedAid);

    if (customAmountValue > 0) {
      const curBal = outstandingBalances[selectedInvoiceId] || {
        patientOutstanding: 0,
        medAidOutstanding: 0,
        totalOutstanding: 0,
        paidAmount: 0,
      };
      const updated = { ...curBal };
      if (creditNoteTo === "patient") {
        updated.patientOutstanding = Math.max(0, curBal.patientOutstanding - customAmountValue);
      } else {
        updated.medAidOutstanding = Math.max(0, curBal.medAidOutstanding - customAmountValue);
      }
      updated.totalOutstanding = updated.patientOutstanding + updated.medAidOutstanding;
      updated.paidAmount = curBal.paidAmount + customAmountValue;

      creditNoteInvoiceLines.push({
        _id: selectedInvoiceId,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        creditNoteAmount: parseFloat(customAmountValue.toFixed(2)),
        paymentSource: creditNoteTo,
        creditNoteReference: referenceNumber,
        creditNoteDate,
        outstandingBalance: {
          patientOutstanding: updated.patientOutstanding,
          medAidOutstanding: updated.medAidOutstanding,
          totalOutstanding: updated.totalOutstanding,
          paidAmount: updated.paidAmount,
        },
      });
    }

    // 4️⃣ Compute new unallocatedBalance
    //    mirror receipts: if they chose unallocatedFunds, subtract; else add
    const newUnallocBalance =
      paymentMethod === "unallocatedFunds"
        ? parseFloat((openingBalance + totalSelectedAmount).toFixed(2))
        : parseFloat(openingBalance.toFixed(2));

    // 5️⃣ Assemble the creditNote payload
    const creditNoteData = {
      creditNoteDate,
      creditNoteTotalAmount: totalSelectedAmount,
      creditNotePaymentMethod: paymentMethod,
      creditNoteReference: referenceNumber,
      creditNotePracticeNumber: invoice.practice?.practiceNumber || "",
      creditNotePracticeVAT: invoice.practice?.vatNumber || "",
      accountId: accountSelected._id,
      unAllocatedBalance: newUnallocBalance,
      creditNoteInvoiceLines,
    };

    setIsSubmitting(true);
    try {
      // 6️⃣ Send it off
      await createCreditNote(creditNoteData);

      // 7️⃣ Update local balances + status exactly as in receipts
      const updatedBalances = { ...outstandingBalances };
      const alloc = totalSelectedAmount;
      const source = !creditNoteTo || creditNoteTo === "patient" ? "patient" : "medicalAid";
      const bal = updatedBalances[selectedInvoiceId] || {};

      if (source === "patient") {
        bal.patientOutstanding = bal.patientOutstanding - alloc;
      } else {
        bal.medAidOutstanding = bal.medAidOutstanding - alloc;
      }
      bal.totalOutstanding = bal.patientOutstanding + bal.medAidOutstanding;
      bal.paidAmount += alloc;

      // update the one invoice's status
      const inv = invoiceList.find((inv) => inv._id === selectedInvoiceId);
      if (inv) {
        if (bal.totalOutstanding < 0) {
          inv.status = "Partially Paid"; // overcredited
        } else if (bal.totalOutstanding === 0) {
          inv.status = "Paid";
        } else if (bal.paidAmount > 0) {
          inv.status = "Partially Paid";
        }
      }

      setOutstandingBalances(updatedBalances);
      showToast("success", "Credit Note created successfully!");
      setHiddenForSuccess(true);
      setTimeout(() => {
        onHide();
        navigate(0);
      }, 2500);
    } catch (error) {
      console.error("Failed to create credit note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  const F = (x) => {
    if (x == null) return "";
    const num = parseFloat(x);
    if (isNaN(num)) return "";
    return `R ${num.toFixed(2)}`;
  };

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    onHide();
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const selectedInvoice = selectedInvoiceId
    ? invoiceList.find((inv) => inv._id === selectedInvoiceId)
    : null;

  return (
    <>
      <Modal show={show && !hiddenForSuccess} onHide={handleClose} dialogClassName="modal-85w">
        <Modal.Header closeButton={!closeAlert}>
          <Modal.Title className="text-primary">Credit Note</Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between mx-0 mb-0 fs-6 fw-normal border-0 rounded-0"
          >
            <span>
              Are you sure you want to close? Changes will not be saved.
            </span>
            <div className="ms-auto d-flex">
              <Button
                variant="outline-primary"
                className="btn btn-outline-primary me-1"
                onClick={handleCancelYes}
              >
                <FaCheck />
              </Button>
              <Button
                variant="outline-secondary"
                className="btn btn-outline-secondary me-1"
                onClick={handleCancelNo}
              >
                <FaTimes />
              </Button>
            </div>
          </Alert>
        )}

        <Modal.Body>
          <Form>
            {!accountSelected ? (
              <>
                <h6 className="text-primary mb-2">Search for an Account</h6>
                <SearchAccounts onAccountSelect={setAccountSelected} />
              </>
            ) : (
              <>
                <Row>
                  {/* Left Column: Account Info */}
                  <Col md={5} className="border-end pe-4">
                    <h5 className="text-primary mb-1">
                      {accountSelected.accountFullNames}{" "}
                      {accountSelected.accountLastName}
                    </h5>
                    <Row>
                      <Col md={6} className="mb-1">
                        <p>
                          <strong>Account No:</strong>{" "}
                          {accountSelected.accountNumber}
                        </p>
                        <p>
                          {accountSelected.accountPostalAddress1}
                          <br />
                          {accountSelected.accountPostalAddress2}
                          <br />
                          {accountSelected.accountPostalAddressCity}
                          <br />
                          {accountSelected.accountPostalAddressPostalCode}
                          <br />
                          {accountSelected.accountEmail}
                          <br />
                          {accountSelected.accountTelNumberCell}
                          <br />
                        </p>
                      </Col>
                      <Col md={6} className="mb-1">
                        <Row>
                          <Col md={6}>
                            <p>
                              <strong>Medical Aid:</strong>{" "}
                            </p>
                          </Col>
                          <Col md={6}>
                            {accountSelected.accountMedicalAid ? (
                              <p>
                                {accountSelected.accountMedicalAid}
                                <br />
                                {accountSelected.accountMedicalAidNumber}
                                <br />
                                {accountSelected.accountMedicalAidPlan}
                                <br />
                                {accountSelected.accountMedicalAidOption}
                                <br />
                              </p>
                            ) : (
                              <p className="text-muted">Private</p>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Credit Note To and Date */}
                    <Row className="mb-2 align-items-center">
                      <Col xs="auto">
                        <span className="fw-bold">Credit Note to:</span>{" "}
                        {creditNoteTo ? (
                          <span className="fw-bold text-primary">
                            {creditNoteTo === "patient" ? "Patient" : "Medical Aid"}
                          </span>
                        ) : (
                          <span className="text-muted">
                            Select an invoice amount
                          </span>
                        )}
                      </Col>
                      <Col xs="auto" className="ms-auto d-flex align-items-center gap-2">
                        <span className="fw-bold">Credit Note Date:</span>
                        <Form.Control
                          type="date"
                          size="sm"
                          value={creditNoteDate}
                          onChange={(e) => setCreditNoteDate(e.target.value)}
                          style={{ width: "auto" }}
                        />
                      </Col>
                    </Row>
{selectedInvoice?.status === "Paid" && (
  <Form.Group className="ms-1 mt-2" controlId="paymentMethod">
    <Form.Label className="fw-bold">Payment Method:</Form.Label>
    <ButtonGroup className="d-flex gap-1">
      <Button
        variant={paymentMethod === "cash" ? "primary" : "outline-secondary"}
        name="cash"
        onClick={handlePaymentMethod}
        style={{ width: "18%" }}
      >
        Cash
      </Button>
      <Button
        variant={paymentMethod === "eft" ? "primary" : "outline-secondary"}
        name="eft"
        onClick={handlePaymentMethod}
        style={{ width: "18%" }}
      >
        EFT
      </Button>
      {creditNoteTo === "patient" && (
        <Button
          variant={
            paymentMethod === "unallocatedFunds"
              ? "primary"
              : "outline-secondary"
          }
          name="unallocatedFunds"
          onClick={handlePaymentMethod}
          style={{ width: "20%" }}
        >
          Unallocated Funds
        </Button>
      )}
    </ButtonGroup>

    {/* validation feedback */}
    {errors.paymentMethod && (
      <Form.Control.Feedback
        type="invalid"
        style={{ display: "block", marginTop: ".25rem" }}
      >
        {errors.paymentMethod}
      </Form.Control.Feedback>
    )}
  </Form.Group>
)}

                    {selectedInvoice?.status === "Paid" && (
                      <Row className="mt-3 align-items-center">
                        <Col xs={4} className="fw-bold">
                          Unallocated Balance:
                        </Col>
                        <Col xs={4} className="text-end">
                          R{" "}
                          {account?.unallocatedBalance != null
                            ? Number(account?.unallocatedBalance).toFixed(2)
                            : "0.00"}
                        </Col>
                      </Row>
                    )}
                    <Form.Group
                      as={Row}
                      className="mt-3 align-items-center"
                      controlId="totalSelected"
                    >
                      <Form.Label column xs={4} className="fw-bold">
                        Total Selected:
                      </Form.Label>

                      <Col xs={4}>
                        <Form.Control
                          plaintext
                          readOnly
                          className="text-end"
                          value={F(totalSelectedAmount)}
                          isInvalid={!!errors.totalSelectedAmount}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.totalSelectedAmount}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>

                    {totalUnallocated > 0 &&
                      paymentMethod === "unallocatedFunds" && (
                        <Row className="mt-2 align-items-center">
                          <Col xs={4} className="fw-bold">
                            Total Unallocated:
                          </Col>
                          <Col xs={4}>
                            <Form.Control
                              className="text-end"
                              size="sm"
                              type="text"
                              value={F(totalUnallocated)}
                              disabled
                            />
                          </Col>
                        </Row>
                      )}

                    <Row className="mt-2 align-items-center">
                      <Col xs={4} className="fw-bold">
                        Reference Number:
                      </Col>
                      <Col xs={6}>
                        <Form.Control
                          type="text"
                          placeholder="Enter payment reference"
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value)}
                          isInvalid={!!errors.referenceNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors?.referenceNumber}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  </Col>

                  {/* Right Column: Invoice Table */}
                  <Col md={7}>
                    <p className="text-muted mt-2">
                      Click on either <strong>Patient</strong> or{" "}
                      <strong>Medical Aid</strong> amount to select for
                      crediting. Selected cells will be highlighted in blue.
                    </p>
                    {practiceOptions?.length > 0 && (
                      <Form.Group as={Row} className="mb-2">
                        <Form.Label column sm={1}>
                          Practice:
                        </Form.Label>
                        <Col sm={4}>
                          <Form.Select
                            value={selectedPractice}
                            onChange={(e) =>
                              setSelectedPractice(e.target.value)
                            }
                            disabled={selectedInvoices?.length > 0} // disables when a cell is selected
                          >
                            {practiceOptions?.map((practice, idx) => (
                              <option key={idx} value={practice.practiceNumber}>
                                {practice.practiceName}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    )}

                    {invoiceList?.length === 0 ? (
                      <p className="text-muted">
                        No invoices found for this account.
                      </p>
                    ) : (
                      <Table bordered size="sm" className="mt-2">
                        <thead className="table-primary text-center">
                          <tr>
                            <th className="text-primary">Date</th>
                            <th className="text-primary">Invoice #</th>
                            <th className="text-primary">Status</th>
                            <th className="text-primary">Allocated</th>
                            <th className="text-primary">Patient</th>
                            <th className="text-primary">Medical Aid</th>
                            <th className="text-primary">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceList
                            .filter(
                              (inv) =>
                                inv.practice?.practiceNumber ===
                                  selectedPractice &&
                                inv.status !== "Pending" &&
                                // if none selected, show all;
                                // otherwise only show the one that’s open
                                (!selectedInvoiceId ||
                                  inv._id === selectedInvoiceId)
                            )
                            .map((inv) => {
                              const invId = inv._id;
                              const isOpen = selectedInvoiceId === invId;
                              // …rest of your rendering as before…

                              // existing allocation & balances
                              const allocAmt =
                                allocations[invId]?.creditNoteAmount || 0;
                              const bal = outstandingBalances[invId] || {};
                              const patOutstanding =
                                bal.patientOutstanding || 0;
                              const medOutstanding = bal.medAidOutstanding || 0;
                              const totOutstanding = bal.totalOutstanding || 0;
                              return (
                                <React.Fragment key={invId}>
                                  {/* ——— Invoice header row ——— */}
                                  <tr
                                    className={`cursor-pointer ${
                                      isOpen ? "fw-semibold" : ""
                                    }`}
                                    onClick={() => toggleInvoice(invId)}
                                  >
                                    <td>
                                      {new Date(
                                        inv.invoiceDate
                                      ).toLocaleDateString()}
                                    </td>
                                    <td>{inv.invoiceNumber}</td>
                                    <td>{inv.status}</td>
                                    <td className="text-end">{F(allocAmt)}</td>
                                    <td className="text-end">
                                      {F(patOutstanding)}
                                    </td>
                                    <td className="text-end">
                                      {F(medOutstanding)}
                                    </td>
                                    <td className="text-end">
                                      {F(totOutstanding)}
                                    </td>
                                  </tr>

                                  {isOpen &&
                                    inv.lines?.map((line) => {
                                      const key = `${inv._id}_${line.lineNumber}`;
                                      const desc = line.description || "";
                                      const truncatedDesc =
                                        desc?.length > 58
                                          ? desc.slice(0, 58) + "…"
                                          : desc;

                                      const selType = selectedLineCells[key];
                                      const isPatientSel =
                                        selType === "patient";
                                      const isMedSel = selType === "medicalAid";
                                      const lineTotal =
                                        (line.patientAmount || 0) +
                                        (line.medAidAmount || 0);

                                      return (
                                        <tr
                                          key={key}
                                          className={
                                            isPatientSel || isMedSel
                                              ? "bg-primary text-white"
                                              : ""
                                          }
                                        >
                                          <td
                                            colSpan={3}
                                            className="ps-4 text-start"
                                          >
                                            {truncatedDesc}
                                            {line.tariffCode &&
                                              ` — ${line.tariffCode}`}
                                          </td>
                                          <td></td>
                                          <td
                                            className={`text-end ${
                                              line.patientAmount > 0
                                                ? "cursor-pointer"
                                                : "text-muted"
                                            } ${
                                              isPatientSel
                                                ? "bg-primary text-white"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              line.patientAmount > 0 &&
                                              toggleLineCell(
                                                inv._id,
                                                line.lineNumber,
                                                "patient"
                                              )
                                            }
                                          >
                                            {line.patientAmount > 0
                                              ? F(line.patientAmount)
                                              : ""}
                                          </td>
                                          <td
                                            className={`text-end ${
                                              line.medAidAmount > 0 &&
                                              !selectedInvoice?.status.includes(
                                                "Processed"
                                              )
                                                ? "cursor-pointer"
                                                : "text-muted"
                                            } ${
                                              isMedSel
                                                ? "bg-primary text-white"
                                                : ""
                                            } ${
                                              selectedInvoice?.status.includes(
                                                "Processed"
                                              )
                                                ? "disabled"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              line.medAidAmount > 0 &&
                                              !selectedInvoice?.status.includes(
                                                "Processed"
                                              ) &&
                                              toggleLineCell(
                                                inv._id,
                                                line.lineNumber,
                                                "medicalAid"
                                              )
                                            }
                                            style={{
                                              pointerEvents:
                                                selectedInvoice?.status.includes(
                                                  "Processed"
                                                )
                                                  ? "none"
                                                  : "auto",
                                            }}
                                          >
                                            {line.medAidAmount > 0
                                              ? F(line.medAidAmount)
                                              : ""}
                                          </td>
                                          <td> </td>
                                        </tr>
                                      );
                                    })}

                                  {/* Custom credit amount row */}
                                  {isOpen && (() => {
                                    const dir = creditNoteTo;
                                    const isPatientDisabled =
                                      dir === "medicalAid" ||
                                      (dir !== "patient" && parseCustom(customCreditMedAid) > 0);
                                    const isMedAidDisabled =
                                      dir === "patient" ||
                                      (dir !== "medicalAid" && parseCustom(customCreditPatient) > 0) ||
                                      selectedInvoice?.status?.includes("Processed");

                                    const patVal = parseCustom(customCreditPatient);
                                    const medVal = parseCustom(customCreditMedAid);

                                    return (
                                      <tr>
                                        <td colSpan={3} className="ps-4 fst-italic text-muted">
                                          Enter custom credit amount
                                        </td>
                                        <td></td>
                                        <td
                                          className={`text-end p-0 ${
                                            patVal > 0 ? "bg-primary text-white" : ""
                                          }`}
                                        >
                                          <input
                                            name="customCreditPatient"
                                            type="text"
                                            value={customCreditPatient}
                                            onChange={handleCustomChange}
                                            onBlur={() => handleCustomBlur("patient")}
                                            disabled={isPatientDisabled}
                                            placeholder="R 0.00"
                                            style={{
                                              border: "none",
                                              outline: "none",
                                              background: "transparent",
                                              width: "100%",
                                              textAlign: "right",
                                              fontSize: "inherit",
                                              padding: "0 0.25rem",
                                              color: patVal > 0 ? "white" : "inherit",
                                            }}
                                          />
                                        </td>
                                        <td
                                          className={`text-end p-0 ${
                                            medVal > 0 ? "bg-primary text-white" : ""
                                          }`}
                                        >
                                          <input
                                            name="customCreditMedAid"
                                            type="text"
                                            value={customCreditMedAid}
                                            onChange={handleCustomChange}
                                            onBlur={() => handleCustomBlur("medicalAid")}
                                            disabled={isMedAidDisabled}
                                            placeholder="R 0.00"
                                            style={{
                                              border: "none",
                                              outline: "none",
                                              background: "transparent",
                                              width: "100%",
                                              textAlign: "right",
                                              fontSize: "inherit",
                                              padding: "0 0.25rem",
                                              color: medVal > 0 ? "white" : "inherit",
                                            }}
                                          />
                                        </td>
                                        <td></td>
                                      </tr>
                                    );
                                  })()}

                                  {isOpen && inv.receipts?.length > 0 && (
                                    <tr key="movement-header">
                                      <td colSpan={7} className="fw-bold">
                                        Movement:
                                      </td>
                                    </tr>
                                  )}

                                  {/* Receipts / Credit-Notes / Refunds */}
                                  {isOpen &&
                                    inv.receipts?.map((r, idx) => {
                                      const isRefund = Boolean(r.refundNumber);
                                      const rawAmt =
                                        r.receiptAmount ??
                                        r.creditNoteAmount ??
                                        r.refundAmount ??
                                        0;
                                      const displayAmt = isRefund
                                        ? `R ${rawAmt.toFixed(2)}`
                                        : `(R ${rawAmt.toFixed(2)})`;
                                      const isPatient =
                                        r.paymentSource === "patient";

                                      return (
                                        <>
                                          <tr key={`receipt-${idx}`}>
                                            <td
                                              colSpan={3}
                                              className="position-relative"
                                            >
                                              <span className="fst-italic fw-semibold ms-3">
                                                {r.receiptNumber ||
                                                  r.creditNoteNumber ||
                                                  r.refundNumber}{" "}
                                                (
                                                {r.receiptDate ||
                                                  r.creditNoteDate ||
                                                  r.refundDate}
                                                )
                                              </span>
                                            </td>
                                            <td></td>

                                            {/* Patient column */}
                                            <td
                                              className={`text-end ${
                                                isPatient
                                                  ? "fst-italic fw-semibold"
                                                  : "text-muted"
                                              }`}
                                            >
                                              {isPatient ? displayAmt : ""}
                                            </td>

                                            {/* Medical-Aid column */}
                                            <td
                                              className={`text-end ${
                                                !isPatient
                                                  ? "fst-italic fw-semibold"
                                                  : "text-muted"
                                              }`}
                                            >
                                              {!isPatient ? displayAmt : ""}
                                            </td>

                                            <td></td>
                                          </tr>
                                        </>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                        </tbody>
                      </Table>
                    )}

                    <p className="text-muted">
                      You can only create Credit Notes for amounts not being
                      processed at the medical aid.
                    </p>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="gap-2 justify-content-start">
          <Col md={6}>
            <Button
              variant="primary"
              onClick={handleCreditNoteCreate}
              className="w-25"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Spinner animation="border" size="sm" role="status" />
              ) : (
                <>
                  <i className="fa-solid fa-square-plus me-2"></i>
                  Create
                </>
              )}
            </Button>
          </Col>
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
